import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { PershingTranslationService, LogService } from '@pershing/pershing-core';
import { ActionMenuService } from '../services/action-menu.service';
import { ActionMenuContext } from '../models/nxp-action-menu-context.model';
import { NxpActionMenuConfig } from '../utils/nxp-action-menu.config';

@Component({
    selector: 'nxp-action-menu',
    templateUrl: './nxp-action-menu.component.html',
    providers: [ActionMenuService]
})

export class NxpActionMenuComponent implements OnInit {
    @Input() context: ActionMenuContext;
    @Input() payload: any;
    @Input() featureMenus: any[];
    @Output() actionMenuSelectEmitter: EventEmitter<any> = new EventEmitter();
    menuContext: ActionMenuContext;
    actionMenuList: any;
    domain: any;
    value: any;
    divider: any[] = [NxpActionMenuConfig.NXP_ACTION_MENU_MASTER.divider];

    constructor(private actionMenuService: ActionMenuService, private translate: PershingTranslationService,
                private logService: LogService) {
        this.translate.addTranslationFolder('@pershing/pershing-common', 'node_modules/@pershing/pershing-common/lib');
    }

    ngOnInit() {
        this.initiateMenus();
    }

    initiateMenus() {
        /*  If payload contains the context data, populate it in actionMenuContext */
        if (this.context) {
            this.menuContext = this.context;
        } else {
            this.menuContext.type = this.payload.contextType ? this.payload.contextType : null;
            this.menuContext.subtype = this.payload.contextSubtype ? this.payload.contextSubtype : null;
            this.menuContext.status = this.payload.contextStatus ? this.payload.contextStatus : null;
            this.menuContext.actionMenuFlags = this.payload.actionMenuFlags ? this.payload.actionMenuFlags : null;
            this.menuContext.key = this.payload.actionMenuKey ? this.payload.actionMenuKey : null;
            this.menuContext.value = this.payload.contextValue ? this.payload.contextValue : null;
        }

        if (!this.menuContext.key || this.menuContext.key.length === 0) {
            if (this.menuContext && this.menuContext.type) {
                this.menuContext.type = this.menuContext.type.toLowerCase().trim();
                if (this.menuContext.subtype) {
                    this.menuContext.subtype = this.menuContext.subtype.toLowerCase().trim();
                    this.menuContext.subtype = this.menuContext.subtype.replace(' ', '');
                }
            } else {
                this.logService.error('Invalid context data is supplied. Exiting ...');
                return;
            }
        }

        /* Get the list of action menus from the action menu meta data based on the context type and flags */
        this.actionMenuList = this.actionMenuService.getActionMenuList(
            this.menuContext, this.menuContext.actionMenuFlags, this.featureMenus);
            /* If the feature needs custom action menus specific to itself, then add them on top of standard menus */
            /* The feature specific menus should be supplied by the feature itself using the same object structure */
            /* For feature menus, the entitlement check should be done by features */
            /* Merge the standard action menus and the feature menus. Put the feature menus at the top */

        if (this.featureMenus != null && this.featureMenus.length > 0) {
            /*
                Handle disabled flag for feature menus.
                If disabled flag is true, then do not add the menu item.
                If feature menu is available in actionMenuList, then remove from feature menu list
            */

            this.featureMenus = this.featureMenus.filter(
                featureMenu => featureMenu.disabled !== true
                    && this.actionMenuList.findIndex(actionMenu => actionMenu.menuId === featureMenu.menuId) < 0);

            if (this.actionMenuList && this.actionMenuList.length > 0) {
                this.actionMenuList = [...this.featureMenus, ...this.divider, ...this.actionMenuList];
            } else {
                this.actionMenuList = this.featureMenus;
            }
        }
    }

    selectMenuAction(menuId: any) {
        const selectedMenu = this.actionMenuList.find(element => element.menuId === menuId);

        if (!selectedMenu) {
            this.logService.error('Action Menu: Selected menu is not found in menu list. Unknown error.');
            return;
        }

        this.handleMenuAction(selectedMenu); // Delegate processing to a diff function for nit testing
    }

    handleMenuAction(selectedMenu: any) {
        let params: any;
        let actionMenuEvent: any;
        let menuId: string;
        let parameterString: string;
        const parameterArray: string[] = new Array();
        let navTarget: string;

        /*
            Below is the old implementation that calls the service to initiate the navigation.
            But the navigaiton should be happening through events instead of calling navigationservice. */
        // this.actionMenuService.handleActionMenuClick(this.context.type, this.context.value, menuId);
        menuId = selectedMenu.menuId;

        if (selectedMenu.gotoPage) {
            navTarget = selectedMenu.gotoPage;
        } else {
            navTarget = '';
        }

        /* Get the target page and params */
        if (this.payload) {
            if (this.payload.gotoParams) {
                params = [];
                const paramString: string = this.payload.gotoParams;
                const ParamValueArray = paramString.split('&');
                ParamValueArray.forEach(paramElement => {
                    const paramArray: string[] = paramElement.split('=');
                    params[paramArray[0]] = paramArray[1];
                });
            } else {
                params = this.payload;
            }
        }


        /* Get context type and value */
        /*
        The domain context is meant for target application and not for the menu.
        So it has be defined/manages separately without mixing it with the type/subtype passed to identify menu items.
        */
        /* If the menu is defined with a domain context type, then use it. If not, base it off the context type passed */
        if (selectedMenu.domain) {
            this.domain = selectedMenu.domain;
        } else if (this.menuContext.type) {
            this.domain = this.menuContext.type.toLowerCase();
        }

        /* Get the value from the action menu context value attribute */
        if (this.menuContext.value) {
            this.value = this.menuContext.value;
        }

        /* Prepare the params */
        const urlParams = new Map();
        if (selectedMenu.payloadMetadata) {
            if (selectedMenu.payloadMetadata.params) {
                const paramsList: string[] = selectedMenu.payloadMetadata.params.split(',');
                let paramValue: string;

                paramsList.forEach(element => {
                    element = element.trim();
                    if (element.indexOf('=') !== -1) {
                        const paramsArray = element.split('=');
                        if (paramsArray && paramsArray.length === 2) {
                            /*
                            For cases where the parameter name that needs to be passed to target application and the
                            attribute coming from the component are different, we indicate the value with {}.

                            Eg: qty={quantity} - qty is the parameter that target expects whereas the component model has it as quantity.

                            For these cases, take the name within the paranthesis and try to find if the incoming payload
                            has the value for this attribute and pack it.
                            */
                            if (paramsArray[1].indexOf('{') !== -1 && paramsArray[1].indexOf('}') !== -1) {
                                const paramName = paramsArray[1].substring(1, paramsArray[1].length - 1);
                                if (params && params[paramName]) {
                                    paramValue = params[paramName];
                                }
                            } else {
                                paramValue = paramsArray[1];
                            }
                            urlParams.set(paramsArray[0], paramValue);
                        } else {
                            this.logService.error('Action Menu: Erroneos payload metadata configuration. Check and correct.');
                        }
                    } else {
                        if (params && params[element]) {
                            urlParams.set(element, params[element]);
                        }
                    }
                });
            }
        }

        if (urlParams && urlParams.size > 0) {
            // parameterString = urlParams.toString();
            urlParams.forEach((mapValue, mapKey) => {
                parameterArray.push(mapKey + '=' + mapValue );
            });
            parameterString = parameterArray.join('&');
        }

        // this.logService.log('Action Menu - Params: ', params);

        /*
            There are 3 ways the selected action will be handled.
            1. Output of ActionMenu through the EventEmitter - Component can make use of this if they do not want to use CustomEvent.
            2. If a callbackEvent is passed, then publish a custom event with the label supplied.
            3. A custom event for "portal.gotoview" to offload the initiation of naigation to portal with the payload.

            Both 1 & 2 passes the selected menu ID and the parameters from Payload as event payload.
            3 has additional items in payload to support navigation.

        /* If component has requested for an event call back, create and publish the event. */

        const eventEmitterPayload = {
            selectedMenu: menuId,
            parameters: parameterString,
            gotoParams: parameterString,
            payload: this.payload
        };

        /* TODO: Decide which value to return */
        if (selectedMenu.callbackEvent) {
            /* Pass the payload as an event */
            actionMenuEvent = new CustomEvent(selectedMenu.callbackEvent, {
                detail: {
                    selectedMenu: menuId,
                    parameters: parameterString,
                    gotoParams: parameterString,
                    payload: this.payload
                }
            });
            document.dispatchEvent(actionMenuEvent);
            this.actionMenuSelectEmitter.emit(eventEmitterPayload); // Emit an event. Global Search uses this to close itself.
            return;
        }

        /* If a navigation target is avaialble, then publish a portal.gotoview event */
        if (navTarget && navTarget.length > 0) {
            actionMenuEvent = new CustomEvent('portal.gotoview', {
                detail: {
                    selectedMenu: menuId,
                    domain: this.domain,
                    value: this.value,
                    gotoPage: navTarget,
                    parameters: parameterString,
                    gotoParams: parameterString
                }
            });
            document.dispatchEvent(actionMenuEvent);
            this.actionMenuSelectEmitter.emit(eventEmitterPayload); // Emit an event. Global Search uses this to close itself.
            return;
        }

        /*
            if neither a target is provided nor a custom event is supplied, it means that the component
            would handled the output. So let us use EventEmitter to provide the selected menu ID and
            params to the components.
        */
        this.actionMenuSelectEmitter.emit(eventEmitterPayload);

        return;
    }

}
